import {useContext, useEffect, useRef, useState} from "react";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DoneIcon from "@mui/icons-material/Done";
import { translate } from "../../../translate/translate";
import StepperComponent, { Steps } from "./components/analysis-step.componentl";
import AnalysisCustomerPageComponent from "./components/analysis-customer.component";
import { toast } from "react-toastify";
import AnalysisForecastPageComponent from "./components/analysis-forecast.component";
import AnalysisPriorityPageComponent from "./components/analysis-priority.component";
import { AnalysisData, AnalysisPriority } from "../../../models/general/analysis-priority";
import { BaseService } from "../../../services/base-service";
import { URLS } from "../../../services/app-urls";
import CustomButtonsDialogVersion from "../../../components/dialog/custom-buttons-dialog.component";
import {AuthContext} from "../../../contexts/auth-context";
import AnalysisPeriodPageComponent from "./components/analysis-period.component";
import { AnalysisPeriod } from "../../../../app/models/general/analysis";
import { orchestrator } from "../../../websocket/orchestrator";


export default function AnalysisPage() {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const service = new BaseService<AnalysisData>(URLS.ANALYSIS_CREATE);

    const [activeStep, setActiveStep] = useState(0);

    const [selectedCustomer, setSelectedCustomer] = useState<{ id: number | null; name: string | null }>({ id: null, name: null });
    const [selectedPeriod, setSelectedPeriod] = useState<{ week: number }>({ week: AnalysisPeriod.SIXTEEN_WEEKS });
    const [selectedForecast, setSelectedForecast] = useState<{ id: number | null; name: string | null }>({ id: null, name: null });
    const [dataPriority, setDataPriority] = useState<AnalysisPriority[]>([]);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);

    const [description, setDescription] = useState("");
    const [newOption, setNewOption] = useState(false);
    const [emptyQuantity, setEmptyQuantity] = useState(0);
    const [isProcessingAnalysis, setIsProcessingAnalysis] = useState(false);
    const isProcessingAnalysisRef = useRef(false);

    useEffect(() => {
        return () => {
            setActiveStep(0);
            setSelectedCustomer({ id: null, name: null });
            setSelectedForecast({ id: null, name: null });
            setDataPriority([]);
        };
    }, []);

    const handleCloseConfirmDialog = (result: string) => {
        if (isProcessingAnalysisRef.current) return;
        setOpenModalConfirm(false);
        if (result==="import") {
            navigate("/general/import-spreadsheets");
        }
        if (result==="continue") {
            handleProcessAnalysis();
        }
    };

    const handleOpenConfirmDialog = () => {
        if (isProcessingAnalysisRef.current) return;
        if (emptyQuantity === 0) {
            setOpenModalConfirm(false);
            handleProcessAnalysis();
        } else {
            setOpenModalConfirm(true);
        }
    };

    const handleNext = () => {
        if (activeStep === Steps.Customer && selectedCustomer.id === null) {
            toast.error(translate.t("select_customer_error"), {
                autoClose: false,
            });
            return;
        }
        if (activeStep === Steps.Forecast && selectedForecast.id === null) {
            toast.error(translate.t("select_forecast_error"), {
                autoClose: false,
            });
            return;
        }
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleProcessAnalysis = () => {
        if (isProcessingAnalysisRef.current) return;
        if (activeStep === Steps.Priority && dataPriority.length === 0) {
            toast.error(translate.t("priority_empty"), {
                autoClose: false,
            });
            return;
        }
        createAnalysis();
    };

    const analysisData = (): AnalysisData | null => {
        if (!selectedForecast.id) return null;
        return {
            forecast_id: selectedForecast.id,
            period: selectedPeriod.week,
            priorities: dataPriority.map((item) => {
                return {
                    component_id: item?.final_product?.id,
                    priority: item.priority,
                };
            }),
            user_id: user.id,
        };
    };

    const createAnalysis = (): void => {
        if (isProcessingAnalysisRef.current) {
            return;
        }
        if (analysisData() === null) {
            return;
        }
        isProcessingAnalysisRef.current = true;
        setIsProcessingAnalysis(true);
        service
            .save(analysisData())
            .then((r) => {
                const analysisId = r.data.analysis_id;
                if (!analysisId) return;
                orchestrator.addConnection({
                    route: 'analysis',
                    id: analysisId,
                    storageKey: `analysis_${analysisId}`,
                });
                navigate(`/general/analysis/${analysisId}/processing`, { replace: true });
            })
            .catch((error) => {
                console.error("Error refreshing notifications:", error);
                isProcessingAnalysisRef.current = false;
                setIsProcessingAnalysis(false);
            });
    };

    const handleSelectCustomer = (customerId: number, customerName: string) => {
        setSelectedCustomer({ id: customerId, name: customerName });
    };

    const handleSelectForecast = (forecastId: number | null, forecastDescription: string | null) => {
        setSelectedForecast({ id: forecastId, name: forecastDescription });
    };

    const handleSelectPriority = (newData: AnalysisPriority[], emptyData: AnalysisPriority[]) => {
        setDataPriority(newData);
        const result = emptyData.map(item => item.bom_number).join(", ");

        setEmptyQuantity(emptyData.length);

        setNewOption(true);
        setDescription(result);
    };

  const handleSelectPeriod = (week: number) => {
    setSelectedPeriod({ week: week });
  };

    const stepsComponents = [
        {
            component: <AnalysisCustomerPageComponent handleSelectCustomer={handleSelectCustomer} selectedCustomer={selectedCustomer} />,
            title: "select_customer",
        },
        {
            component: <AnalysisForecastPageComponent handleSelectForecast={handleSelectForecast} selectedForecast={selectedForecast} selectedCustomer={selectedCustomer.name} />,
            title: "select_forecast",
        },
        {
          component: <AnalysisPeriodPageComponent selectedPeriod={selectedPeriod} handleSelectPeriod={handleSelectPeriod} />,
          title: "select_period",
        },
        {
          component: <AnalysisPriorityPageComponent forecastId={selectedForecast.id} handleSelectPriority={handleSelectPriority} />,
          title: "organize_by_priority",
        }
    ];

    return (
        <section>
            <div className="floating-title floating-title-item" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBackIcon color="primary" fontSize="large" fontWeight="fontWeightBold" />
                </IconButton>
                <Typography variant={"h5"}>{translate.t("new_analysis")}</Typography>
                <Grid item sx={{ display: "flex", justifyContent: "flex-end", flexGrow: 1 }}>
                    <Button color="primary" variant="outlined" onClick={() => navigate(-1)}>
                        {translate.t("cancel")}
                    </Button>
                    {
                        activeStep === Steps.Priority ? (
                            <Button color="primary" variant="contained" onClick={handleOpenConfirmDialog} disabled={dataPriority.length === 0 || isProcessingAnalysis} sx={{ ml: 2 }}>
                                <DoneIcon />
                                {translate.t("processs_analysis")}
                            </Button>
                        ) : (
                            <Button color="primary" variant="contained" onClick={handleNext} sx={{ ml: 2 }} >
                                {translate.t("next")}
                                <ArrowForwardIcon />
                            </Button>
                        )
                    }
                </Grid>
            </div>
            <StepperComponent activeStep={activeStep} setActiveStep={setActiveStep} />
            <Typography variant="h6" sx={{ mt: 4 }} color="primary" style={{ fontWeight: 500, fontSize: "16px" }}>{translate.t(stepsComponents[activeStep].title)}</Typography>
            <Box sx={{ mt: 1 }}>{stepsComponents[activeStep].component}</Box>

            {openModalConfirm && (
                <CustomButtonsDialogVersion
                    id="dialog-confirm-analysis"
                    open={openModalConfirm}
                    colorButton={"primary"}
                    onClose={handleCloseConfirmDialog}
                    title={translate.t("missing_bom_detected")}
                    message={translate.t("missing_bom_text")}
                    description={description ? description + "." : "N/A"}
                    labelButton={translate.t("proceed_regardless")}
                    newOption={newOption}
                />
            )}
        </section>
    );
}
